import { useEffect } from "react"
import { createPortal } from "react-dom"
import { X } from "lucide-react"
import { useModalOpen } from "../lib/useModalOpen"
import { NewActivityForm, type NewActivityFormMode } from "./NewActivityForm"

export function NewActivityModal({
  open,
  onClose,
  mode,
  onSuccess,
}: {
  open: boolean
  onClose: () => void
  mode: NewActivityFormMode
  onSuccess?: () => void
}) {
  useModalOpen(open)

  useEffect(() => {
    if (!open) return
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", handleEsc)
    return () => window.removeEventListener("keydown", handleEsc)
  }, [open, onClose])

  if (!open) return null

  const handleSuccess = () => {
    onSuccess?.()
    onClose()
  }

  const handleCancel = () => {
    onClose()
  }

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 glass-backdrop-md"
        onClick={onClose}
        aria-hidden
      />
      <div className="relative w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col bg-black/60 backdrop-blur-2xl rounded-3xl shadow-2xl border border-white/[0.08]">
        <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-white/[0.06]">
          <h2 className="text-base font-mono font-bold text-white/90 uppercase tracking-widest">
            New Activity
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl text-white/40 hover:text-white/80 hover:bg-white/[0.06] transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto p-6">
          <NewActivityForm
            mode={mode}
            onSuccess={handleSuccess}
            onCancel={mode === "admin" ? handleCancel : undefined}
          />
        </div>
      </div>
    </div>,
    document.body
  )
}

/** @deprecated Use NewActivityModal instead. */
export const NewReportModal = NewActivityModal
